package org.zq.linkgame.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import org.zq.linkgame.ui.theme.*

/**
 * User avatar icon (default profile)
 */
@Composable
fun UserAvatarIcon(
    modifier: Modifier = Modifier,
    size: Dp = 30.dp,
    color: Color = Color.White
) {
    Canvas(modifier = modifier.size(size)) {
        val center = Offset(size.toPx() / 2, size.toPx() / 2)
        val strokeWidth = 2.dp.toPx()
        
        // Draw head (circle)
        val headRadius = size.toPx() * 0.22f
        val headCenterY = center.y - size.toPx() * 0.12f
        drawCircle(
            color = color,
            radius = headRadius,
            center = Offset(center.x, headCenterY),
            style = Stroke(width = strokeWidth)
        )
        
        // Draw body (arc)
        val bodyTop = headCenterY + headRadius + size.toPx() * 0.08f
        val bodyWidth = size.toPx() * 0.6f
        val bodyHeight = size.toPx() * 0.35f
        
        val path = Path().apply {
            moveTo(center.x - bodyWidth / 2, size.toPx() * 0.85f)
            quadraticTo(
                center.x - bodyWidth / 2, bodyTop,
                center.x, bodyTop
            )
            quadraticTo(
                center.x + bodyWidth / 2, bodyTop,
                center.x + bodyWidth / 2, size.toPx() * 0.85f
            )
        }
        
        drawPath(
            path = path,
            color = color,
            style = Stroke(width = strokeWidth, cap = StrokeCap.Round)
        )
    }
}

/**
 * Settings gear icon
 */
@Composable
fun SettingsGearIcon(
    modifier: Modifier = Modifier,
    size: Dp = 30.dp,
    color: Color = Color.White
) {
    Canvas(modifier = modifier.size(size)) {
        val center = Offset(size.toPx() / 2, size.toPx() / 2)
        val outerRadius = size.toPx() * 0.4f
        val innerRadius = size.toPx() * 0.25f
        val teethCount = 8
        val strokeWidth = 3.dp.toPx()
        
        // Draw outer circle
        drawCircle(
            color = color,
            radius = outerRadius,
            center = center,
            style = Stroke(width = strokeWidth)
        )
        
        // Draw inner circle
        drawCircle(
            color = color,
            radius = innerRadius,
            center = center,
            style = Stroke(width = strokeWidth)
        )
        
        // Draw gear teeth
        for (i in 0 until teethCount) {
            val angle = (i * 360f / teethCount) * (kotlin.math.PI / 180).toFloat()
            val startX = center.x + (outerRadius - strokeWidth) * kotlin.math.cos(angle)
            val startY = center.y + (outerRadius - strokeWidth) * kotlin.math.sin(angle)
            val endX = center.x + (outerRadius + strokeWidth * 2) * kotlin.math.cos(angle)
            val endY = center.y + (outerRadius + strokeWidth * 2) * kotlin.math.sin(angle)
            
            drawLine(
                color = color,
                start = Offset(startX, startY),
                end = Offset(endX, endY),
                strokeWidth = strokeWidth,
                cap = StrokeCap.Round
            )
        }
    }
}

/**
 * Close/X icon
 */
@Composable
fun CloseIcon(
    modifier: Modifier = Modifier,
    size: Dp = 30.dp,
    color: Color = DarkGray
) {
    Canvas(modifier = modifier.size(size)) {
        val padding = size.toPx() * 0.25f
        val strokeWidth = 6.dp.toPx()
        
        // Draw X
        drawLine(
            color = color,
            start = Offset(padding, padding),
            end = Offset(size.toPx() - padding, size.toPx() - padding),
            strokeWidth = strokeWidth,
            cap = StrokeCap.Round
        )
        drawLine(
            color = color,
            start = Offset(size.toPx() - padding, padding),
            end = Offset(padding, size.toPx() - padding),
            strokeWidth = strokeWidth,
            cap = StrokeCap.Round
        )
    }
}

/**
 * Triangle icon for navigation
 */
@Composable
fun TriangleIcon(
    modifier: Modifier = Modifier,
    size: Dp = 50.dp,
    color: Color = DeepGreen
) {
    Canvas(modifier = modifier.size(size)) {
        val path = Path().apply {
            val width = size.toPx()
            val height = size.toPx()
            moveTo(width * 0.2f, height * 0.5f)
            lineTo(width * 0.8f, height * 0.2f)
            lineTo(width * 0.8f, height * 0.8f)
            close()
        }
        drawPath(path, color)
    }
}

/**
 * Lock icon for locked level packs
 */
@Composable
fun LockIcon(
    modifier: Modifier = Modifier,
    size: Dp = 40.dp,
    color: Color = DarkGray
) {
    Canvas(modifier = modifier.size(size)) {
        val strokeWidth = 3.dp.toPx()
        val lockBodyWidth = size.toPx() * 0.6f
        val lockBodyHeight = size.toPx() * 0.45f
        val lockBodyLeft = (size.toPx() - lockBodyWidth) / 2
        val lockBodyTop = size.toPx() * 0.45f
        
        // Draw lock body
        drawRoundRect(
            color = color,
            topLeft = Offset(lockBodyLeft, lockBodyTop),
            size = Size(lockBodyWidth, lockBodyHeight),
            cornerRadius = androidx.compose.ui.geometry.CornerRadius(4.dp.toPx())
        )
        
        // Draw lock shackle
        val shackleWidth = size.toPx() * 0.4f
        val shackleHeight = size.toPx() * 0.35f
        val shackleLeft = (size.toPx() - shackleWidth) / 2
        
        drawArc(
            color = color,
            startAngle = 180f,
            sweepAngle = 180f,
            useCenter = false,
            topLeft = Offset(shackleLeft, size.toPx() * 0.15f),
            size = Size(shackleWidth, shackleHeight),
            style = Stroke(width = strokeWidth, cap = StrokeCap.Round)
        )
        
        // Draw vertical lines of shackle
        drawLine(
            color = color,
            start = Offset(shackleLeft, size.toPx() * 0.15f + shackleHeight / 2),
            end = Offset(shackleLeft, lockBodyTop),
            strokeWidth = strokeWidth
        )
        drawLine(
            color = color,
            start = Offset(shackleLeft + shackleWidth, size.toPx() * 0.15f + shackleHeight / 2),
            end = Offset(shackleLeft + shackleWidth, lockBodyTop),
            strokeWidth = strokeWidth
        )
    }
}

/**
 * Hint bulb icon for game props
 */
@Composable
fun HintBulbIcon(
    modifier: Modifier = Modifier,
    size: Dp = 50.dp
) {
    Canvas(modifier = modifier.size(size)) {
        val bulbColor = Yellow
        val centerX = size.toPx() / 2
        val bulbRadius = size.toPx() * 0.3f
        
        // Draw bulb
        drawCircle(
            color = bulbColor,
            radius = bulbRadius,
            center = Offset(centerX, size.toPx() * 0.35f)
        )
        
        // Draw bulb base
        drawRoundRect(
            color = DarkGray,
            topLeft = Offset(centerX - size.toPx() * 0.15f, size.toPx() * 0.6f),
            size = Size(size.toPx() * 0.3f, size.toPx() * 0.2f),
            cornerRadius = androidx.compose.ui.geometry.CornerRadius(4.dp.toPx())
        )
        
        // Draw light rays
        val rayColor = Yellow.copy(alpha = 0.7f)
        val rayLength = size.toPx() * 0.15f
        val rayStroke = 3.dp.toPx()
        
        for (i in 0 until 8) {
            val angle = (i * 45f - 90f) * (kotlin.math.PI / 180).toFloat()
            val startX = centerX + (bulbRadius + 5.dp.toPx()) * kotlin.math.cos(angle)
            val startY = size.toPx() * 0.35f + (bulbRadius + 5.dp.toPx()) * kotlin.math.sin(angle)
            val endX = centerX + (bulbRadius + rayLength) * kotlin.math.cos(angle)
            val endY = size.toPx() * 0.35f + (bulbRadius + rayLength) * kotlin.math.sin(angle)
            
            drawLine(
                color = rayColor,
                start = Offset(startX, startY),
                end = Offset(endX, endY),
                strokeWidth = rayStroke,
                cap = StrokeCap.Round
            )
        }
    }
}

/**
 * Bomb icon for game props
 */
@Composable
fun BombIcon(
    modifier: Modifier = Modifier,
    size: Dp = 50.dp
) {
    Canvas(modifier = modifier.size(size)) {
        val bombColor = Red
        val centerX = size.toPx() / 2
        val bombRadius = size.toPx() * 0.3f
        
        // Draw bomb body
        drawCircle(
            color = bombColor,
            radius = bombRadius,
            center = Offset(centerX, size.toPx() * 0.55f)
        )
        
        // Draw fuse
        val path = Path().apply {
            moveTo(centerX, size.toPx() * 0.25f)
            quadraticTo(
                centerX + size.toPx() * 0.15f,
                size.toPx() * 0.15f,
                centerX + size.toPx() * 0.1f,
                size.toPx() * 0.3f
            )
        }
        drawPath(
            path = path,
            color = DarkGray,
            style = Stroke(width = 3.dp.toPx(), cap = StrokeCap.Round)
        )
        
        // Draw spark
        drawCircle(
            color = Yellow,
            radius = size.toPx() * 0.06f,
            center = Offset(centerX, size.toPx() * 0.2f)
        )
        drawCircle(
            color = OrangeRed,
            radius = size.toPx() * 0.04f,
            center = Offset(centerX, size.toPx() * 0.2f)
        )
    }
}

/**
 * Shuffle/cards icon for game props
 */
@Composable
fun ShuffleIcon(
    modifier: Modifier = Modifier,
    size: Dp = 50.dp
) {
    Canvas(modifier = modifier.size(size)) {
        val cardColor = Blue
        val cardWidth = size.toPx() * 0.35f
        val cardHeight = size.toPx() * 0.5f
        
        // Draw back card
        drawRoundRect(
            color = cardColor.copy(alpha = 0.6f),
            topLeft = Offset(size.toPx() * 0.15f, size.toPx() * 0.2f),
            size = Size(cardWidth, cardHeight),
            cornerRadius = androidx.compose.ui.geometry.CornerRadius(4.dp.toPx())
        )
        
        // Draw front card
        drawRoundRect(
            color = cardColor,
            topLeft = Offset(size.toPx() * 0.35f, size.toPx() * 0.3f),
            size = Size(cardWidth, cardHeight),
            cornerRadius = androidx.compose.ui.geometry.CornerRadius(4.dp.toPx())
        )
        
        // Draw arrows
        val arrowColor = Color.White
        val strokeWidth = 2.dp.toPx()
        
        // Curved arrow
        val arrowPath = Path().apply {
            moveTo(size.toPx() * 0.7f, size.toPx() * 0.25f)
            quadraticTo(
                size.toPx() * 0.85f,
                size.toPx() * 0.4f,
                size.toPx() * 0.65f,
                size.toPx() * 0.5f
            )
        }
        drawPath(
            path = arrowPath,
            color = arrowColor,
            style = Stroke(width = strokeWidth, cap = StrokeCap.Round)
        )
    }
}

/**
 * App logo icon - two connected blocks
 */
@Composable
fun AppLogoIcon(
    modifier: Modifier = Modifier,
    size: Dp = 80.dp
) {
    Canvas(modifier = modifier.size(size)) {
        val blockSize = size.toPx() * 0.35f
        val cornerRadius = androidx.compose.ui.geometry.CornerRadius(8.dp.toPx())
        val strokeWidth = 4.dp.toPx()
        
        // Draw yellow block
        drawRoundRect(
            color = Yellow,
            topLeft = Offset(size.toPx() * 0.1f, size.toPx() * 0.25f),
            size = Size(blockSize, blockSize),
            cornerRadius = cornerRadius
        )
        drawRoundRect(
            color = OrangeRedDark,
            topLeft = Offset(size.toPx() * 0.1f, size.toPx() * 0.25f),
            size = Size(blockSize, blockSize),
            cornerRadius = cornerRadius,
            style = Stroke(width = strokeWidth)
        )
        
        // Draw green block (overlapping)
        drawRoundRect(
            color = Green,
            topLeft = Offset(size.toPx() * 0.4f, size.toPx() * 0.35f),
            size = Size(blockSize, blockSize),
            cornerRadius = cornerRadius
        )
        drawRoundRect(
            color = DeepGreen,
            topLeft = Offset(size.toPx() * 0.4f, size.toPx() * 0.35f),
            size = Size(blockSize, blockSize),
            cornerRadius = cornerRadius,
            style = Stroke(width = strokeWidth)
        )
        
        // Draw connection line
        drawLine(
            color = DeepBlue,
            start = Offset(size.toPx() * 0.45f, size.toPx() * 0.425f),
            end = Offset(size.toPx() * 0.4f, size.toPx() * 0.475f),
            strokeWidth = strokeWidth,
            cap = StrokeCap.Round
        )
    }
}

/**
 * Social media icon base composable
 */
@Composable
fun SocialMediaIcon(
    icon: @Composable () -> Unit,
    backgroundColor: Color,
    modifier: Modifier = Modifier,
    size: Dp = 60.dp
) {
    Box(
        modifier = modifier
            .size(size)
            .clip(CircleShape)
            .background(backgroundColor),
        contentAlignment = Alignment.Center
    ) {
        icon()
    }
}

/**
 * WeChat icon
 */
@Composable
fun WeChatIcon(
    modifier: Modifier = Modifier,
    size: Dp = 60.dp
) {
    SocialMediaIcon(
        icon = {
            Canvas(modifier = Modifier.size(size * 0.6f)) {
                val iconSize = size.toPx() * 0.6f
                // Simplified WeChat logo - two chat bubbles
                drawCircle(
                    color = Color.White,
                    radius = iconSize * 0.25f,
                    center = Offset(iconSize * 0.35f, iconSize * 0.4f)
                )
                drawCircle(
                    color = Color.White,
                    radius = iconSize * 0.2f,
                    center = Offset(iconSize * 0.65f, iconSize * 0.55f)
                )
            }
        },
        backgroundColor = WeChatGreen,
        modifier = modifier,
        size = size
    )
}

/**
 * QQ icon
 */
@Composable
fun QQIcon(
    modifier: Modifier = Modifier,
    size: Dp = 60.dp
) {
    SocialMediaIcon(
        icon = {
            Canvas(modifier = Modifier.size(size * 0.6f)) {
                val iconSize = size.toPx() * 0.6f
                // Simplified QQ logo - penguin shape
                drawOval(
                    color = Color.White,
                    topLeft = Offset(iconSize * 0.2f, iconSize * 0.15f),
                    size = Size(iconSize * 0.6f, iconSize * 0.7f)
                )
            }
        },
        backgroundColor = QQBlue,
        modifier = modifier,
        size = size
    )
}

/**
 * Weibo icon
 */
@Composable
fun WeiboIcon(
    modifier: Modifier = Modifier,
    size: Dp = 60.dp
) {
    SocialMediaIcon(
        icon = {
            Canvas(modifier = Modifier.size(size * 0.6f)) {
                val iconSize = size.toPx() * 0.6f
                // Simplified Weibo logo - eye shape
                drawOval(
                    color = Color.White,
                    topLeft = Offset(iconSize * 0.15f, iconSize * 0.3f),
                    size = Size(iconSize * 0.7f, iconSize * 0.4f)
                )
            }
        },
        backgroundColor = WeiboOrange,
        modifier = modifier,
        size = size
    )
}

/**
 * Game pattern icons for the game grid - 30 unique patterns
 */
@Composable
fun GamePatternIcon(
    patternType: Int,
    modifier: Modifier = Modifier,
    size: Dp = 50.dp
) {
    // 30 distinct, easily distinguishable colors
    val colors = listOf(
        // Primary vivid colors
        Color(0xFFFF0000), // 0 - Pure Red
        Color(0xFF0000FF), // 1 - Pure Blue
        Color(0xFF00CC00), // 2 - Pure Green
        Color(0xFFFFD700), // 3 - Gold Yellow
        Color(0xFFFF00FF), // 4 - Magenta
        Color(0xFF00FFFF), // 5 - Cyan
        Color(0xFFFF6600), // 6 - Orange
        Color(0xFF9900FF), // 7 - Purple
        Color(0xFF00FF00), // 8 - Lime
        Color(0xFFFF0099), // 9 - Hot Pink
        // Secondary distinct colors
        Color(0xFF0066FF), // 10 - Royal Blue
        Color(0xFF663300), // 11 - Brown
        Color(0xFF006666), // 12 - Dark Teal
        Color(0xFFCC0066), // 13 - Raspberry
        Color(0xFF666666), // 14 - Gray
        Color(0xFF99FF00), // 15 - Yellow Green
        Color(0xFF003399), // 16 - Navy Blue
        Color(0xFF990000), // 17 - Dark Red
        Color(0xFF009933), // 18 - Forest Green
        Color(0xFFFF9933), // 19 - Light Orange
        // Tertiary distinct colors
        Color(0xFF330066), // 20 - Dark Purple
        Color(0xFF66CCFF), // 21 - Sky Blue
        Color(0xFFFFCC00), // 22 - Bright Yellow
        Color(0xFF339966), // 23 - Sea Green
        Color(0xFFCC3399), // 24 - Medium Pink
        Color(0xFF996633), // 25 - Tan
        Color(0xFF3366CC), // 26 - Cobalt Blue
        Color(0xFF66FF99), // 27 - Mint Green
        Color(0xFFCC6600), // 28 - Burnt Orange
        Color(0xFF000000)  // 29 - Black
    )
    val color = colors[patternType % colors.size]
    
    // 10 different shapes, cycled for 30 patterns
    Canvas(modifier = modifier.size(size)) {
        when (patternType % 10) {
            0 -> { // Circle
                drawCircle(
                    color = color,
                    radius = size.toPx() * 0.4f,
                    center = Offset(size.toPx() / 2, size.toPx() / 2)
                )
            }
            1 -> { // Square
                drawRoundRect(
                    color = color,
                    topLeft = Offset(size.toPx() * 0.15f, size.toPx() * 0.15f),
                    size = Size(size.toPx() * 0.7f, size.toPx() * 0.7f),
                    cornerRadius = androidx.compose.ui.geometry.CornerRadius(4.dp.toPx())
                )
            }
            2 -> { // Triangle Up
                val path = Path().apply {
                    moveTo(size.toPx() / 2, size.toPx() * 0.1f)
                    lineTo(size.toPx() * 0.9f, size.toPx() * 0.85f)
                    lineTo(size.toPx() * 0.1f, size.toPx() * 0.85f)
                    close()
                }
                drawPath(path, color)
            }
            3 -> { // Diamond
                val path = Path().apply {
                    moveTo(size.toPx() / 2, size.toPx() * 0.1f)
                    lineTo(size.toPx() * 0.9f, size.toPx() / 2)
                    lineTo(size.toPx() / 2, size.toPx() * 0.9f)
                    lineTo(size.toPx() * 0.1f, size.toPx() / 2)
                    close()
                }
                drawPath(path, color)
            }
            4 -> { // Heart
                val path = Path().apply {
                    moveTo(size.toPx() / 2, size.toPx() * 0.85f)
                    quadraticTo(size.toPx() * 0.1f, size.toPx() * 0.5f, size.toPx() * 0.25f, size.toPx() * 0.25f)
                    quadraticTo(size.toPx() / 2, size.toPx() * 0.1f, size.toPx() / 2, size.toPx() * 0.35f)
                    quadraticTo(size.toPx() / 2, size.toPx() * 0.1f, size.toPx() * 0.75f, size.toPx() * 0.25f)
                    quadraticTo(size.toPx() * 0.9f, size.toPx() * 0.5f, size.toPx() / 2, size.toPx() * 0.85f)
                    close()
                }
                drawPath(path, color)
            }
            5 -> { // Oval Horizontal
                drawOval(
                    color = color,
                    topLeft = Offset(size.toPx() * 0.1f, size.toPx() * 0.25f),
                    size = Size(size.toPx() * 0.8f, size.toPx() * 0.5f)
                )
            }
            6 -> { // Triangle Down
                val path = Path().apply {
                    moveTo(size.toPx() * 0.1f, size.toPx() * 0.15f)
                    lineTo(size.toPx() * 0.9f, size.toPx() * 0.15f)
                    lineTo(size.toPx() / 2, size.toPx() * 0.9f)
                    close()
                }
                drawPath(path, color)
            }
            7 -> { // Star (5-pointed)
                val path = Path().apply {
                    val cx = size.toPx() / 2
                    val cy = size.toPx() / 2
                    val outerR = size.toPx() * 0.4f
                    val innerR = size.toPx() * 0.18f
                    for (i in 0 until 10) {
                        val r = if (i % 2 == 0) outerR else innerR
                        val angle = kotlin.math.PI / 2 + i * kotlin.math.PI / 5
                        val x = cx + (r * kotlin.math.cos(angle)).toFloat()
                        val y = cy - (r * kotlin.math.sin(angle)).toFloat()
                        if (i == 0) moveTo(x, y) else lineTo(x, y)
                    }
                    close()
                }
                drawPath(path, color)
            }
            8 -> { // Hexagon
                val path = Path().apply {
                    val cx = size.toPx() / 2
                    val cy = size.toPx() / 2
                    val r = size.toPx() * 0.4f
                    for (i in 0 until 6) {
                        val angle = kotlin.math.PI / 6 + i * kotlin.math.PI / 3
                        val x = cx + (r * kotlin.math.cos(angle)).toFloat()
                        val y = cy - (r * kotlin.math.sin(angle)).toFloat()
                        if (i == 0) moveTo(x, y) else lineTo(x, y)
                    }
                    close()
                }
                drawPath(path, color)
            }
            9 -> { // Cross/Plus
                val thickness = size.toPx() * 0.25f
                val cx = size.toPx() / 2
                val cy = size.toPx() / 2
                // Horizontal bar
                drawRoundRect(
                    color = color,
                    topLeft = Offset(size.toPx() * 0.1f, cy - thickness / 2),
                    size = Size(size.toPx() * 0.8f, thickness),
                    cornerRadius = androidx.compose.ui.geometry.CornerRadius(2.dp.toPx())
                )
                // Vertical bar
                drawRoundRect(
                    color = color,
                    topLeft = Offset(cx - thickness / 2, size.toPx() * 0.1f),
                    size = Size(thickness, size.toPx() * 0.8f),
                    cornerRadius = androidx.compose.ui.geometry.CornerRadius(2.dp.toPx())
                )
            }
        }
    }
}
